import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tag } from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import IssueKeyBadge from "./IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import {
  ISSUE_TYPES,
  STATUS_CATEGORY_COLORS,
} from "../../constants/projects.constants";
import { all_routes } from "../../../router/all_routes";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type { Issue } from "../../../../core/data/redux/projects/types";

interface EpicChildrenListProps {
  epicId: number;
  epicColor: string | null;
}

const EpicChildrenList: React.FC<EpicChildrenListProps> = ({
  epicId,
  epicColor,
}) => {
  const project = useSelector(selectCurrentProject);
  const statuses = useSelector(selectStatuses);
  const activeCompany = useSelector(selectActiveCompany);

  const [children, setChildren] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeCompany.company_id) return;
    let cancelled = false;
    setLoading(true);
    issuesApi
      .listEpicChildren(
        activeCompany.company_id,
        activeCompany.company_type,
        epicId
      )
      .then((data: any) => {
        if (cancelled) return;
        setChildren(Array.isArray(data?.issues) ? data.issues : []);
      })
      .catch((e: any) => {
        if (!cancelled)
          notify.error(errMsg(e, "Failed to load epic children"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [epicId, activeCompany.company_id, activeCompany.company_type]);

  const statusMap = useMemo(
    () => new Map(statuses.map((s) => [s.id, s])),
    [statuses]
  );

  const doneCount = children.filter((c) => {
    const st = statusMap.get(c.status_id);
    return st?.category === "DONE";
  }).length;

  const total = children.length;
  const completionPct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  if (loading) {
    return (
      <div className="text-center py-2">
        <Spin size="small" />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <p className="text-muted small mb-0">
        No issues link to this epic yet. Set the Epic field on a task to add it
        here.
      </p>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2 small text-muted">
        <span>
          {doneCount} of {total} done
        </span>
        <div
          className="progress flex-grow-1"
          style={{ height: 6, background: "#e5e7eb", borderRadius: 4 }}
        >
          <div
            className="progress-bar"
            style={{
              width: `${completionPct}%`,
              background: epicColor || "#8B5CF6",
            }}
          />
        </div>
        <span>{completionPct}%</span>
      </div>

      <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
        {children.map((c) => {
          const status = statusMap.get(c.status_id);
          const typeMeta = ISSUE_TYPES.find((t) => t.value === c.type);
          const path =
            project &&
            all_routes.projectIssueDetails
              .replace(":projectKey", project.key)
              .replace(":issueKey", c.issue_key);
          return (
            <li
              key={c.id}
              className="d-flex align-items-center gap-2 border rounded p-2"
            >
              {typeMeta && (
                <span
                  className="d-inline-block rounded-circle"
                  style={{
                    width: 8,
                    height: 8,
                    background: typeMeta.color,
                  }}
                  title={typeMeta.label}
                />
              )}
              <IssueKeyBadge
                projectKey={project?.key || ""}
                issueKey={c.issue_key}
              />
              {path ? (
                <Link
                  to={path}
                  className="flex-grow-1 text-truncate text-dark text-decoration-none"
                >
                  {c.title}
                </Link>
              ) : (
                <span className="flex-grow-1 text-truncate">{c.title}</span>
              )}
              {status && (
                <Tag
                  color="default"
                  style={{
                    background:
                      status.color || STATUS_CATEGORY_COLORS[status.category],
                    color: "#fff",
                    border: "none",
                    margin: 0,
                  }}
                >
                  {status.name}
                </Tag>
              )}
              {c.assignee_snapshot?.name && (
                <UserAvatar
                  name={c.assignee_snapshot.name}
                  img={c.assignee_snapshot.img}
                  size={22}
                  fontSize={11}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default EpicChildrenList;
